import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Plus, Search, User, LogOut, Home, Coins, Trophy, Hammer } from "lucide-react";
import { isPlyxStandaloneMode, isPlyxProductionDomain } from "@/lib/plyxStandalone";
import PlyxExitModal from "@/components/plyx/PlyxExitModal";

const PlyxLogo = () => (
  <Link to="/plyx" className="inline-flex items-center gap-2" data-testid="plyx-header-logo">
    <svg width="28" height="28" viewBox="0 0 64 64" fill="none">
      <defs>
        <linearGradient id="plyxHeaderGrad" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FF2D7A"/>
          <stop offset="0.5" stopColor="#FFB42D"/>
          <stop offset="1" stopColor="#00E0C7"/>
        </linearGradient>
      </defs>
      <polygon points="14,10 14,54 52,32" fill="url(#plyxHeaderGrad)"/>
    </svg>
    <span className="font-display text-xl text-white tracking-tight">Plyx</span>
  </Link>
);

const PlyxHeader = () => {
  const { user, logout } = usePlyxAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [exitOpen, setExitOpen] = useState(false);

  // Hide header on full-screen feed for max immersion (button overlay instead)
  const isFeed = location.pathname === "/plyx";
  if (isFeed) return null;
  // Hide the "Preview · Exit" pill on the real plyx.* production domain;
  // it only makes sense in preview/dev environments.
  const standalone = isPlyxStandaloneMode() && !isPlyxProductionDomain();

  return (
    <>
    <header className="sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/10" data-testid="plyx-header">
      <div className="container-narrow flex items-center justify-between py-3">
        <div className="flex items-center gap-2">
          <PlyxLogo/>
          {standalone && (
            <button
              type="button"
              onClick={() => setExitOpen(true)}
              title="Exit standalone Plyx preview and return to AfriMarket"
              data-testid="plyx-standalone-pill"
              className="ml-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold text-[#00E0C7] border border-[#00E0C7]/40 bg-[#00E0C7]/10 hover:bg-[#00E0C7]/20 hover:text-white hover:border-[#00E0C7]/70 transition-colors cursor-pointer"
            >
              <span>Preview</span>
              <span aria-hidden="true" className="opacity-60">·</span>
              <span className="text-white/80">Exit</span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link to="/plyx" className="text-white/70 hover:text-white transition-colors" data-testid="plyx-nav-feed" title="Feed">
            <Home className="w-5 h-5"/>
          </Link>
          <Link to="/plyx/challenges" className="text-white/70 hover:text-white transition-colors hidden sm:inline-flex" data-testid="plyx-nav-challenges" title="Challenges">
            <Trophy className="w-5 h-5"/>
          </Link>
          <Link to="/plyx/makers" className="text-white/70 hover:text-white transition-colors hidden sm:inline-flex" data-testid="plyx-nav-makers" title="Makers · Talent discovery">
            <Hammer className="w-5 h-5"/>
          </Link>
          {user ? (
            <>
              <Link
                to="/plyx/wallet"
                data-testid="plyx-nav-coins"
                title={`${(user.coin_balance ?? 0).toLocaleString()} coins`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-[#FFB42D] to-[#FF2D7A] text-[#0A0A0A] hover:opacity-90"
              >
                <Coins className="w-3 h-3" />
                {(user.coin_balance ?? 0).toLocaleString()}
              </Link>
              <Link
                to="/plyx/upload"
                data-testid="plyx-nav-upload"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold text-[#0A0A0A] text-sm"
                style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
              >
                <Plus className="w-4 h-4"/> Upload
              </Link>
              <Link
                to={`/plyx/u/${user.handle}`}
                data-testid="plyx-nav-profile"
                className="text-white/70 hover:text-white transition-colors"
                title={`@${user.handle}`}
              >
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.handle} className="w-7 h-7 rounded-full object-cover"/>
                ) : (
                  <User className="w-5 h-5"/>
                )}
              </Link>
              <button
                type="button"
                onClick={() => { logout(); navigate("/plyx"); }}
                data-testid="plyx-nav-logout"
                className="text-white/40 hover:text-white transition-colors"
                title="Log out"
              >
                <LogOut className="w-4 h-4"/>
              </button>
            </>
          ) : (
            <>
              <Link to="/plyx/login" data-testid="plyx-nav-login" className="text-white/70 hover:text-white text-sm font-medium">Log in</Link>
              <Link
                to="/plyx/register"
                data-testid="plyx-nav-register"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold text-[#0A0A0A] text-sm"
                style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
              >
                Sign up
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
    <PlyxExitModal open={exitOpen} onClose={() => setExitOpen(false)} />
    </>
  );
};

export default PlyxHeader;
